import moment from 'moment'
import { CustomEventTypes } from '@src/config'
import { getCoachDescriptor } from '@src/utils'
import { OnboardingExperience, User, UserPropertyOnboardingExperienceKind } from '@src/types'
import { getVideoCallProductInfo } from '@src/hooks/useVideoCallProductType'
import { CreatingPlan } from './CreatingPlan'
import { PlanDetails } from './PlanDetails/PlanDetails'
import { ScheduleCalls } from './ScheduleCalls'
import { Completed } from './Completed'
import { StepConfig } from './types'

interface BuildStepConfigParams {
  user?: Pick<
    User,
    'uhcMember' | 'features' | 'onboardingExperience' | 'primaryCoachingKind'
  > | null
  userIsUpdatingTheirPlan: boolean
  insurancePolicyIsEligible: boolean
  appointmentRecurrenceExists: boolean
}

export const buildStepsConfig = ({
  user,
  userIsUpdatingTheirPlan,
  insurancePolicyIsEligible,
  appointmentRecurrenceExists,
}: BuildStepConfigParams): StepConfig[] => {
  const { isHealthCoachingVideoCallProductType: isHealthCoaching } = getVideoCallProductInfo(
    user?.features || [],
  )
  const onboardingExperience = user?.onboardingExperience

  const desiredWeight = onboardingExperience?.desiredWeight
  const timeToReachGoal = calculateDateDifference(onboardingExperience?.dateToReachDesiredWeight)

  // Only users in the weight loss experience can update their plan
  if (userIsUpdatingTheirPlan) {
    return [
      {
        screen: CreatingPlan,
        title: 'Updating you Weight Loss Plan',
        bubbleText: 'Calculating your ideal weight loss plan...',
        trackEvent: CustomEventTypes.OnboardingExperienceCreatingPlanScreen,
      },
      {
        screen: PlanDetails,
        title: 'Plan Updated! 🎉',
        bubbleText: 'We’ve personalized your weight loss goal!',
        description: `Based on your responses, we now predict you'll hit your ${desiredWeight} lbs goal in ${timeToReachGoal}!`
          .replace(/\s+/g, ' ')
          .trim(),
        buttonText: 'Go Home',
        showRightAccessory: false,
        trackEvent: CustomEventTypes.OnboardingExperienceUpdatedPlanDetailsScreen,
      },
    ]
  }

  return [
    creatingPlanStep({
      onboardingExperience,
      isHealthCoaching,
    }),
    planDetailsStep({
      onboardingExperience,
      isHealthCoaching,
      insurancePolicyIsEligible,
      user,
      desiredWeight,
      timeToReachGoal,
      appointmentRecurrenceExists,
    }),
    {
      screen: ScheduleCalls,
      title: 'Schedule Recurring Calls',
      buttonText: 'Schedule Recurring Calls',
      showRightAccessory: true,
      trackEvent: CustomEventTypes.OnboardingExperienceScheduleRecurrentCallsScreen,
    },
    {
      screen: Completed,
      bubbleText: 'Calls were scheduled with me successfully!',
      buttonText: 'Go Home',
      showRightAccessory: false,
      trackEvent: CustomEventTypes.OnboardingExperienceConfirmedScreen,
    },
  ]
}

interface PlanStepProps {
  onboardingExperience?: OnboardingExperience | null
  isHealthCoaching?: boolean
  insurancePolicyIsEligible?: boolean
  appointmentRecurrenceExists?: boolean
  user?: BuildStepConfigParams['user']
  desiredWeight?: number | null
  timeToReachGoal?: string
}
const creatingPlanStep = ({ onboardingExperience, isHealthCoaching }: PlanStepProps) => {
  const bubbleText = () => {
    if (onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss) {
      return isHealthCoaching
        ? 'Setting up your weight loss plan...'
        : 'Calculating your ideal weight loss plan...'
    }

    if (isHealthCoaching) {
      return 'Setting up your plan...'
    }

    return 'Looking at your answers to craft the right support plan…'
  }

  return {
    screen: CreatingPlan,
    title: isHealthCoaching
      ? 'Shaping your plan based on your goals'
      : 'Creating a Personalized Plan for You!',
    bubbleText: bubbleText(),
    trackEvent: CustomEventTypes.OnboardingExperienceCreatingPlanScreen,
  }
}
const planDetailsStep = ({
  onboardingExperience,
  isHealthCoaching,
  insurancePolicyIsEligible,
  appointmentRecurrenceExists,
  user,
  desiredWeight,
  timeToReachGoal,
}: PlanStepProps) => {
  const coachDescriptor = getCoachDescriptor(user)
  const planDetailsNonInsuranceDescription = isHealthCoaching
    ? `Visit the ${coachDescriptor.capitalized.hubTitle} to book your next call with your ${coachDescriptor}!`
    : `Visit the ${coachDescriptor.capitalized.hubTitle} to book your call out of pocket with your ${coachDescriptor}!`

  const bubbleText = () => {
    if (!insurancePolicyIsEligible) {
      return undefined
    }

    if (onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss) {
      return "You'll make great progress in just your first 4 calls!"
    }

    return 'You can start feeling real improvements in just your first 4 calls!'
  }
  const planDetailsDescriptionText = () => {
    if (!insurancePolicyIsEligible) {
      return planDetailsNonInsuranceDescription
    }

    switch (onboardingExperience?.kind) {
      case UserPropertyOnboardingExperienceKind.WeightLoss:
        return isHealthCoaching
          ? `You're on track to hit your ${desiredWeight} lbs goal — just by building small, sustainable habits.`
          : `With regular sessions and lifestyle changes, we predict you'll hit your ${desiredWeight} lbs goal in ${timeToReachGoal}!`
      case UserPropertyOnboardingExperienceKind.WomensHealth:
        return 'With regular sessions, you’ll start feeling more balanced and see progress within weeks.'
      default:
        return 'With regular sessions, you’ll build habits and feel more stable — often within weeks.'
    }
  }

  let note: string | undefined
  if (appointmentRecurrenceExists) {
    note = undefined
  } else if (insurancePolicyIsEligible) {
    note = 'Lets start with booking your first calls and we can book the remaining later on!'
  } else {
    note = planDetailsNonInsuranceDescription
  }

  return {
    screen: PlanDetails,
    title: 'Great news! 🎉',
    bubbleText: bubbleText(),
    description: planDetailsDescriptionText().replace(/\s+/g, ' ').trim(),
    note,
    buttonText:
      insurancePolicyIsEligible && !appointmentRecurrenceExists
        ? 'Schedule Recurring Calls'
        : `Back to ${coachDescriptor.capitalized.hubTitle}`,
    showRightAccessory: true,
    trackEvent: CustomEventTypes.OnboardingExperiencePlanDetailsScreen,
  }
}
const calculateDateDifference = (dateString?: string | null): string => {
  if (!dateString) {
    return ''
  }

  const targetDate = moment(dateString)
  const now = moment()

  const weeksDifference = targetDate.diff(now, 'weeks')
  const monthsDifference = targetDate.diff(now, 'months')

  if (weeksDifference < 8) {
    return `${weeksDifference} week${weeksDifference !== 1 ? 's' : ''}`
  } else {
    return `${monthsDifference} month${monthsDifference !== 1 ? 's' : ''}`
  }
}
